/**
 * `ResizeHandle` — thin draggable rail used to horizontally resize the
 * shell's side panes (`SidePanel` left, `RightSidebar` right). Pointer-
 * driven (handles mouse + touch + pen via one event model), wraps the
 * document-level `pointermove`/`pointerup` listeners so the drag keeps
 * working when the cursor leaves the handle itself.
 *
 * The handle is `position: absolute` over the parent's edge (~4px wide
 * hit area, 1px visible). The parent must be `position: relative`. It
 * doesn't manage width state — the consumer owns the state and applies
 * `onResize(deltaX)` to clamp/persist as it sees fit.
 *
 * `side` tells the handle whether a positive cursor delta WIDENS or
 * NARROWS the parent:
 *   - `side: "right"` — handle sits on the parent's right edge (the
 *     case-workspace pane), pulling right WIDENS → pass `dx` through.
 *   - `side: "left"` — handle sits on the parent's left edge (the
 *     right sidebar), pulling left WIDENS → pass `-dx` through.
 */
import { useCallback, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export interface ResizeHandleProps {
  /** Which edge of the parent the handle sits on. */
  side: "left" | "right";
  /** Called on every pointer move during a drag with the SIGNED width
   *  delta in CSS pixels (positive = widen, negative = narrow). The
   *  consumer is responsible for accumulating + clamping. */
  onResize: (widthDelta: number) => void;
  /** Aria label — describe the resize target (e.g. "Resize agents pane"). */
  ariaLabel: string;
  /** Optional aria-valuenow / -valuemin / -valuemax for the separator
   *  role's progress semantics. */
  ariaValueNow?: number;
  ariaValueMin?: number;
  ariaValueMax?: number;
  /** testid the e2e suite can target. */
  testid?: string;
}

export function ResizeHandle(props: ResizeHandleProps) {
  const [dragging, setDragging] = useState(false);
  // Track the last cursor X without re-rendering — every pointermove
  // computes the delta against this ref + updates it in-place.
  const lastXRef = useRef<number>(0);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Only react to primary button / single touch — middle-click +
      // right-click + multi-touch should not initiate a drag.
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      setDragging(true);
      lastXRef.current = e.clientX;

      const onMove = (ev: PointerEvent) => {
        const dx = ev.clientX - lastXRef.current;
        lastXRef.current = ev.clientX;
        if (dx === 0) return;
        props.onResize(props.side === "right" ? dx : -dx);
      };
      const onUp = () => {
        setDragging(false);
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
        document.removeEventListener("pointercancel", onUp);
        // Restore the document chrome (set during the drag below) so
        // text selection works again after release.
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      };

      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
      document.addEventListener("pointercancel", onUp);
      // Lock the cursor + suppress accidental text selection while
      // dragging across other elements.
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
    },
    [props],
  );

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={props.ariaLabel}
      aria-valuenow={props.ariaValueNow}
      aria-valuemin={props.ariaValueMin}
      aria-valuemax={props.ariaValueMax}
      data-testid={props.testid}
      data-dragging={dragging ? "true" : undefined}
      onPointerDown={onPointerDown}
      // The handle is wider than its visible line so it's easy to grab
      // (~6px hit area) but only the centre 1px is painted.
      className={cn(
        "absolute top-0 z-20 h-full w-1.5 cursor-col-resize touch-none select-none",
        props.side === "right" ? "-right-[3px]" : "-left-[3px]",
        // Painted strip lives via ::after so the hit area can stay
        // wider than the visible line.
        "after:absolute after:top-0 after:h-full after:w-px after:bg-transparent after:transition-colors",
        props.side === "right"
          ? "after:right-[3px]"
          : "after:left-[3px]",
        dragging
          ? "after:bg-blue-500/60"
          : "hover:after:bg-cream-300 dark:hover:after:bg-coffee-700",
      )}
    />
  );
}
